<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import BaseCluster from '~/components/foundations/BaseCluster.vue'
import BaseIcon from '~/components/foundations/BaseIcon.vue'
import BaseStack from '~/components/foundations/BaseStack.vue'
import TextBody from '~/components/foundations/TextBody.vue'
import TextTitle from '~/components/foundations/TextTitle.vue'
import BaseBreadcrumbs from '~/components/molecules/BaseBreadcrumbs.vue'
import type { BreadcrumbItem } from '~/components/molecules/BaseBreadcrumbs.vue'
import BaseIconMenu from '~/components/molecules/BaseIconMenu.vue'
import type { MenuItem } from '~/components/molecules/BaseIconMenu.vue'
import ConfirmDialog from '~/components/molecules/ConfirmDialog.vue'
import SetIdentificationDialog from '~/components/organisms/SetIdentificationDialog.vue'
import { useLoading } from '~/composables/useLoading'
import { useToast } from '~/composables/useToast'
import type { DailyWorkFile } from '~/entities/dailyWorkFile'
import type { WorkFileIdentification } from '~/entities/workFileIdentification'
import { InvalidStatusError, DataNotFoundError } from '~/repositories/error'
import { updateWorkFileIdentifications } from '~/repositories/workFileIdentificationRepository'
import { revertDailyWorkFile } from '~/services/revertDailyWorkFileService'
import { byKeyComparer } from '~/utils/compare'
import { formatDateWithWeekday } from '~/utils/date'

const props = defineProps<{
  checkFileTitle: string
  workDate?: string
  timeboxName?: string
  checkSheetDisplayTitle?: string
  enabledMenu?: boolean
  dailyWorkFile?: DailyWorkFile
  identifications?: WorkFileIdentification[]
  breadcrumbItems?: BreadcrumbItem[]
}>()

const emit = defineEmits<{
  refreshIdentifications: []
}>()

const { t } = useI18n()
const { withLoading } = useLoading()
const { addToast } = useToast()

const showWorkFileMenu = ref(false)
const showDeleteConfirmDialog = ref(false)
const showDeleteAlertDialog = ref(false)
const showSetIdentificationDialog = ref(false)

const isSubmitted = computed(() => props.dailyWorkFile?.status === 'SUBMITTED')
const canDeleteWorkFile = computed(
  () => props.enabledMenu && props.dailyWorkFile && !isSubmitted.value
)
const canEditIdentifications = computed(
  () => props.enabledMenu && (props.identifications?.length ?? 0) > 0 && !isSubmitted.value
)
const deleteMenuLabel = computed(() => (props.identifications ? t('削除') : t('未着手に戻す')))
const deleteMessage = computed(() =>
  props.identifications
    ? t('入力途中の記録は削除されます。削除してもよろしいでしょうか？')
    : t('入力途中の記録は削除されます。未着手に戻してよろしいでしょうか？')
)

const workFileMenu = computed<MenuItem[]>(
  () =>
    [
      canEditIdentifications.value && {
        label: t('識別情報の変更'),
        onClick: () => {
          showWorkFileMenu.value = false
          showSetIdentificationDialog.value = true
        },
      },
      canDeleteWorkFile.value && {
        label: deleteMenuLabel.value,
        onClick: () => {
          showWorkFileMenu.value = false
          showDeleteConfirmDialog.value = true
        },
        color: 'danger',
      },
    ].filter(Boolean) as MenuItem[]
)

const deleteWorkFile = async () => {
  showWorkFileMenu.value = false
  showDeleteConfirmDialog.value = false

  const type = props.dailyWorkFile?.check_file_type
  const organizationId = props.dailyWorkFile?.organization_id
  const checkFileId = props.dailyWorkFile?.check_file_id

  const isSuccess = await withLoading(async () => {
    if (props.dailyWorkFile) {
      try {
        await revertDailyWorkFile(props.dailyWorkFile.id)
      } catch (error) {
        if (error instanceof InvalidStatusError) {
          return false
        }
        if (error instanceof DataNotFoundError) {
          addToast({
            message: t('未着手に戻せませんでした。'),
            color: 'danger',
          })
          return false
        }

        throw error
      }
    }

    return true
  })

  if (!isSuccess) {
    showDeleteAlertDialog.value = true
    return
  }

  // Datadogで検出された「未着手に戻す」処理中に別画面へ移動するイレギュラー操作への対応
  // organizationId / checkFileId は現在ルートから継承せず、削除対象の作業記録から明示する
  if (type === 'SCHEDULED') {
    return await navigateTo({
      name: 'organizationId-checkFiles-checkFileId-workFiles',
      params: {
        organizationId,
        checkFileId,
      },
    })
  } else if (type === 'AS_NEEDED') {
    return await navigateTo({
      name: 'organizationId-checkFiles-checkFileId-asNeededWorkFiles',
      params: {
        organizationId,
        checkFileId,
      },
    })
  }
}

const updateIdentifications = async (identifications: WorkFileIdentification[]) => {
  showSetIdentificationDialog.value = false

  await withLoading(async () => {
    if (!props.dailyWorkFile) {
      return
    }
    await updateWorkFileIdentifications(
      identifications.map((identification) => {
        return {
          ...identification,
          id: identification.id,
          work_file_id: props.dailyWorkFile!.id,
        }
      })
    )
    emit('refreshIdentifications')
  })
}

const identificationDisplayList = computed(
  () =>
    props.identifications
      ?.map((identification) => ({
        id: identification.id,
        position: identification.position,
        name: identification.display_name,
        value: identification.value,
      }))
      .sort(byKeyComparer('position')) ?? []
)
</script>

<template>
  <BaseStack gap="s" class="work-file-details">
    <BaseBreadcrumbs v-if="breadcrumbItems" :items="breadcrumbItems" />
    <BaseStack gap="xxs">
      <BaseCluster justify="space-between">
        <TextTitle size="medium">
          {{ checkFileTitle }}
        </TextTitle>
        <BaseIconMenu
          v-if="workFileMenu.length > 0"
          v-model:is-active="showWorkFileMenu"
          :menu-items="workFileMenu"
          width="140px"
          anchor="right"
        >
          <template #icon>
            <BaseIcon :type="'more'" />
          </template>
        </BaseIconMenu>
      </BaseCluster>
      <TextBody v-for="identification in identificationDisplayList" :key="identification.id">
        {{ `${identification.name}: ${identification.value}` }}
      </TextBody>
      <TextBody v-if="workDate">{{ formatDateWithWeekday(workDate) }}</TextBody>
      <TextBody v-if="timeboxName">{{ $t('実施タイミング') + `: ${timeboxName}` }}</TextBody>
      <TextBody v-if="checkSheetDisplayTitle">
        {{ $t('チェックシート') + `: ${checkSheetDisplayTitle}` }}
      </TextBody>
    </BaseStack>
    <ConfirmDialog
      v-if="showDeleteConfirmDialog"
      v-model:is-active="showDeleteConfirmDialog"
      :ok-button="{ color: 'danger', text: deleteMenuLabel }"
      :title="deleteMenuLabel"
      :message="deleteMessage"
      @ok="deleteWorkFile"
      @cancel="() => (showDeleteConfirmDialog = false)"
    />
    <SetIdentificationDialog
      v-if="dailyWorkFile && canEditIdentifications"
      v-model:is-active="showSetIdentificationDialog"
      :check-file-id="dailyWorkFile.check_file_id"
      :organization-id="dailyWorkFile.organization_id"
      :location-id="dailyWorkFile.location_id"
      :identifications="identifications ?? []"
      edit-mode
      @submit="updateIdentifications"
    />
  </BaseStack>
</template>

<style lang="scss" scoped>
.work-file-details {
  padding-top: spacingToken.$spacing-m;
}
</style>
